
import { Vue, Options } from "vue-class-component";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import * as Emoji from "quill-emoji";
import emojiMap from "./emoji-map";
import { PusatResolusiV2Controller } from "@/app/ui/controllers/PusatResolusiV2Controller";
import { MentionsData } from "@/domain/entities/PusatResolusiV2";
import MagicUrl from "quill-magic-url";
import { SendChatPusatResolusiRequest } from "@/data/payload/api/PusatResolusiV2ApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import he from "he";

interface User {
  id: string;
  name: string;
}

@Options({
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    isSendButton: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:modelValue"],
  watch: {
    modelValue(newVal: string) {
      const conditionWatch =
        this.quill &&
        !this.quill.hasFocus() &&
        newVal !== this.quill.root.innerHTML;
      if (conditionWatch) {
        this.quill.root.innerHTML = newVal;
      }
    }
  }
})
export default class ChatBoxMessage extends Vue {
  modelValue!: string;
  isSendButton!: boolean;
  users: MentionsData[] = [];
  quill: Quill | null = null;
  showMentionList = false;
  mentionQuery = "";
  previewFile: any[] = [];
  isLoading = false;
  isLoadingMention = false;

  get mentionSelected() {
    return this.isSendButton ? PusatResolusiV2Controller.mentionSelected : PusatResolusiV2Controller.mentionSelectedFinish;
  }

  get uploadedFiles() {
    return this.isSendButton ? PusatResolusiV2Controller.uploadedFiles : PusatResolusiV2Controller.uploadedFilesFinish;
  }

  mounted() {
    this.resetValue();
    // Register the custom blot and emoji module after Quill is imported
    const EmojiBlot = Quill.import("blots/embed");

    class CustomEmojiBlot extends EmojiBlot {
      static create(value: any): HTMLElement {
        const node = super.create() as HTMLElement;
        const parseUnicode = (unicode: string): any => {
          return unicode.split("-").map((str) => parseInt(str, 16));
        };

        if (typeof value === "object") {
          node.innerText = String.fromCodePoint(parseUnicode(value.unicode));
        } else if (typeof value === "string") {
          const valueObj = emojiMap[value];
          if (valueObj) {
            node.innerText = String.fromCodePoint(
              parseUnicode(valueObj.unicode)
            );
          }
        }

        return node;
      }

      static value(node: HTMLElement): string {
        return node.innerText;
      }

      static blotName = "emoji";
      static className = "emoji";
      static tagName = "span";
    }

    // Register modules after defining CustomEmojiBlot
    Quill.register({
      "formats/emoji": CustomEmojiBlot,
      "modules/emoji": Emoji,
      "modules/magicUrl": MagicUrl
    });

    const editorEl = this.$refs.editorRef as HTMLDivElement;
    const toolbarEl = this.$refs.toolbarRef as HTMLDivElement;

    if (!editorEl || !toolbarEl) {
      console.error("Editor or toolbar not found");
      return;
    }

    this.quill = new Quill(editorEl, {
      theme: "snow",
      placeholder: "Masukkan pesan Anda",
      modules: {
        toolbar: toolbarEl,
        "emoji-toolbar": true,
        "emoji-shortname": true,
        magicUrl: {
          // Regex used to check URLs during typing
          urlRegularExpression: /(https?:\/\/[\S]+)|(www.[\S]+)|(tel:[\S]+)/g,
          // Regex used to check URLs on paste
          globalRegularExpression: /(https?:\/\/|www\.|tel:)[\S]+/g
        }
      }
    });

    if (this.modelValue) {
      const delta = (this.quill.clipboard as any).convert(this.modelValue);
      this.quill.setContents(delta);
    }

    this.quill.on("text-change", () => {
      const html = this.quill?.root.innerHTML;

      this.$emit("update:modelValue", html);
      this.scrollToBottom();
    });
  }

  filterUsers() {
    if (!this.mentionQuery) {
      this.showMentionList = false;
      return;
    }
    if (this.mentionQuery.length > 1) {
      //search user locally
      const searchTerm = this.mentionQuery.replace("@", "").toLowerCase();
      this.users = this.users.filter((user) =>
        user.name.toLowerCase().includes(searchTerm)
      );
      if (this.users.length > 0 && this.isLoadingMention === false) {
        this.showMentionList = false;
      }
      return;
    }
    this.getMentionData();
  }

  selectMention(user: MentionsData) {
    this.showMentionList = false;
    this.mentionSelected.push(user);
    this.mentionQuery = "";
  }

  removeMention(code: string) {
    const index = this.mentionSelected.findIndex(
      (mention) => mention.code === code
    );
    if (index !== -1) {
      this.mentionSelected.splice(index, 1);
    }
  }

  onBlurMention() {
    this.showMentionList = false;
    this.mentionQuery = "";
  }

  removeHtmlChars(str: string) {
    // Remove HTML tags
    const text = str.replaceAll(/<[^>]*>/g, "");
    // Decode HTML entities
    const decodedText = he.decode(text);
    return decodedText;
  }

  get isDisableSend() {
    return (
      this.removeHtmlChars(this.modelValue).length < 1 ||
      this.countChar > 1000 ||
      this.isLoading
    );
  }

  get countChar() {
    const mentionLength = this.mentionSelected.reduce(
      (acc, mention) => acc + mention.code.length,
      0
    );
    return this.removeHtmlChars(this.modelValue).length + mentionLength;
  }

  scrollToBottom() {
    if (!this.quill) return;
    const editor = this.quill.root;
    editor.scrollTop = editor.scrollHeight;
  }

  showEmojiPicker() {
    if (!this.quill) return;
    const toolbar = document.querySelector(".ql-toolbar");
    const emojiBtn = toolbar?.querySelector(".ql-emoji") as HTMLElement;
    if (emojiBtn) {
      emojiBtn.click(); // Programmatically trigger the original button
    }
  }

  handleFileUpload(event: any) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files) return;
    const maxSize = 5 * 1024 * 1024; // 5 MB
    const maxFiles = 3;
    for (let i = 0; i < files.length; i++) {
      if (this.uploadedFiles.length >= maxFiles) {
        break;
      }
      if (files[i].size > maxSize) {
        MainAppController.showErrorMessage(
          new ErrorMessageEntities({
            image: "error-token-ngen",
            message: "Sesuaikan dan upload ulang file anda",
            title: "Ukuran file melebihi 5 MB",
            buttonSuccessText: "Oke, mengerti",
            onClose: () => {
              MainAppController.closeErrorMessage();
            }
          })
        );
        continue;
      }
      if (files[i].type.startsWith("image/")) {
        const reader = new FileReader();
        let base64String = "";
        reader.onload = (e: any) => {
          base64String = e.target.result;
          this.previewFile.push({ ...files[i], data: base64String });
        };
        reader.readAsDataURL(files[i]);
      } else {
        this.previewFile.push(files[i]);
      }
      this.uploadedFiles.push(files[i]);
    }
    input.value = "";
  }

  removeFile(id: number) {
    const index = this.uploadedFiles.findIndex((file, index) => index === id);
    if (index !== -1) {
      this.previewFile.splice(index, 1);
      this.uploadedFiles.splice(index, 1);
    }
  }
  fileExt(val: string) {
    return val.split(".")[1];
  }

  getFileColor(ext?: string): string {
    if (!ext) return "#EDEDED";
    const getExtFile = this.fileExt(ext);

    const lowerExt = getExtFile.toLowerCase();
    switch (lowerExt) {
      case "doc":
      case "docx":
        return "#155EEF";
      case "pdf":
        return "#AD0C00";
      case "xlsx":
        return "#016F3E";
      case "xls":
        return "#079455";
      case "csv":
        return "#EDEDED";
      default:
        return "#EDEDED";
    }
  }

  get id() {
    return Number(this.$route.params.id);
  }

  async getMentionData() {
    try {
      this.isLoadingMention = true;
      this.showMentionList = true;
      if (this.mentionQuery.startsWith("@") && this.mentionQuery.length < 2) {
        let response = await PusatResolusiV2Controller.getMention(this.id);
        if (this.mentionSelected.length > 0) {
          response = response.filter(
            (user) =>
              !this.mentionSelected.some(
                (mention) => mention.code === user.code
              )
          );
        }
        this.users = response;
      }
    } catch (error) {
      this.isLoadingMention = false;
      this.showMentionList = false;
      console.error("Error fetching mention data:", error);
    } finally {
      this.isLoadingMention = false;
      // Clean up the interval after the first execution
    }
  }

  resetValue() {
    PusatResolusiV2Controller.setChatMessage("");
    PusatResolusiV2Controller.setMentionSelected([]);
    PusatResolusiV2Controller.setUploadedFiles([]);
    this.previewFile = [];
  }

  async sendChat() {
    try {
      this.isLoading = true;
      const send = await PusatResolusiV2Controller.sendChat({
        id: this.id,
        payload: new SendChatPusatResolusiRequest({
          message: this.modelValue,
          mentions: JSON.stringify(this.mentionSelected),
          attachments: this.uploadedFiles.filter((e): e is File => e !== null)
        })
      });
      if (send) {
        this.resetValue();
      }
    } catch (error) {
      this.isLoading = false;
      this.$toast.add({
        severity: "error",
        detail: `Gagal mengirim pesan`,
        life: 5000,
        closable: false,
        editable: false
      })
    } finally {
      this.isLoading = false;
    }
  }
}
